import type { StaleBaseReview } from '@colplat/core-client';
import type { FieldVersion, PresenceUser, Uuid } from '@colplat/protocol';
import { escapeHtml } from './dom.js';
import { presenceDotHtml } from './presence.js';

/**
 * History is view-only in this pass: `Session` exposes no "restore an old
 * version" primitive (only `resolveReview` for a stale-base conflict), so
 * there is no restore button here — adding one is a `core-client` change,
 * not a `ui-web` one.
 */
export function historyMarkup(
  label: string,
  history: FieldVersion[],
  review: StaleBaseReview | undefined,
  resolveUser: (id: Uuid) => PresenceUser | undefined,
): string {
  const title = `<div class="cf-hist__title">${escapeHtml(label)} — ${history.length} version${history.length === 1 ? '' : 's'}</div>`;

  const reviewBlock = review
    ? `<div class="cf-review"><div class="cf-review__h">Which value should stand?</div>
        <div class="cf-review__opt"><span>${formatValue(review.theirs.value)}
          <span class="cf-hist__origin">online</span></span>
          <button type="button" class="btn btn-sm btn-outline-secondary cf-hist__restore" data-review-pick="theirs">Keep</button></div>
        <div class="cf-review__opt"><span>${formatValue(review.mine)}
          <span class="cf-hist__origin offline">offline · you</span></span>
          <button type="button" class="btn btn-sm btn-warning cf-hist__restore" data-review-pick="mine">Keep</button></div></div>`
    : '';

  const rows = history.map((version, i) => {
    const user = version.author ? resolveUser(version.author) : undefined;
    const name = user ? escapeHtml(user.name) : originLabel(version.source);
    const dot = user ? presenceDotHtml(user) : '';
    const stale = version.version > 1 && version.baseVersion !== version.version - 1;
    return `<div class="cf-hist__row">
      <div class="cf-hist__top">
        ${dot}
        <span class="cf-hist__val">${formatValue(version.value)}</span>
        ${i === 0 ? '<span class="cf-hist__cur">current</span>' : ''}
      </div>
      <div class="cf-hist__meta">${name} · ${formatWallTime(version.wallTime)}
        <span class="cf-hist__origin${version.source === 'offline' ? ' offline' : ''}">${version.source}</span>
        ${stale ? '<span class="cf-hist__origin" style="border-color:#e0b072;color:#96650f">stale base</span>' : ''}
      </div>
    </div>`;
  }).join('');

  return `${title}${reviewBlock}${rows}`;
}

/** `wallTime` is display metadata only (hard invariant #6) — a full ISO
 * string is exact but far wider than a history row needs. */
function formatWallTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function originLabel(source: FieldVersion['source']): string {
  switch (source) {
    case 'import': return 'Imported';
    case 'external': return 'External';
    default: return 'System';
  }
}

function formatValue(value: FieldVersion['value']): string {
  if (value.t === 'null') return '<em class="text-secondary">empty</em>';
  const text = String(value.v);
  return text === '' ? '<em class="text-secondary">empty</em>' : escapeHtml(text);
}

export function historyButtonHtml(count: number, label: string): string {
  return (
    `<button type="button" class="cf-hist-btn" data-history-toggle aria-expanded="false" ` +
    `aria-label="History for ${escapeHtml(label)}, ${count} version${count === 1 ? '' : 's'}">` +
    `${SHEETS_ICON}<span class="cf-hist-btn__n">${count}</span></button>`
  );
}

const SHEETS_ICON =
  '<svg viewBox="0 0 18 18" width="14" height="14" aria-hidden="true" style="display:block">' +
  '<g fill="var(--cf-surface)" stroke="currentColor" stroke-width="1.15" stroke-linejoin="round">' +
  '<rect x="6.6" y="1.3" width="9.4" height="11" rx="1.5"/>' +
  '<rect x="4.1" y="3.2" width="9.4" height="11" rx="1.5"/>' +
  '<rect x="1.6" y="5.1" width="9.4" height="11" rx="1.5"/></g></svg>';
